import { FC, useContext, useEffect, useRef, useState } from 'react';
import {
  updateWorkflow,
  createWorkflow,
  resetExecutions,
} from 'services/apiHandlers/Workflows/Workflow';
import { Toastify } from 'App';
import * as Yup from 'yup';
import Dropdown from 'components/Dropdown/Dropdown';
import SearchDropdown from 'components/Dropdown/SearchDropdown';
import { getEventNames } from 'services/apiHandlers/Workflows/Workflow';
import { z } from 'zod';
import DatePicker from 'components/Datepicker';
import { WorkflowContext } from 'store/WorkflowContext';
import { useFormik, FormikValues } from 'formik';
import { SaveWorkflowType, WorkflowSchema } from '../index.type';
import { useOutsideClick } from 'app/hooks/useOutsideClick';
import React from 'react';
import moment from 'moment';
import { useLocation, useNavigate } from 'react-router-dom';
import { workflow } from 'services/constant/routes';
import { eventExtraOptions } from 'staticdata/EventLogs/Data';

interface Props {
  onClose: () => void;
  onSave: () => void;
}

const responseSchema = z.object({
  status: z.number(),
  data: z.array(z.string()),
});
type Response = z.infer<typeof responseSchema>;

const createWorkfloResponseSchema = z.object({
  status: z.number(),
  data: WorkflowSchema,
});
type CreateWorkflowResponse = z.infer<typeof createWorkfloResponseSchema>;


const statusOptions = [
  'Disabled',
  "Test Mode (doesn't execute actions)",
  'Live',
];

const propertiesToDelete = [
  'initialConditionGroup',
  'workflow',
  'accountId',
  'id',
  'lastTriggered',
  'lastModified',
];

const SaveModal: FC<Props> = ({ onClose, onSave }) => {
  const { pathname } = useLocation();
  const [options, setOptions] = useState<Array<string>>([]);
  const [savedData, setSavedData] = useState<SaveWorkflowType>({
    defaultConversionReportingEvent: 'Select an item',
    isEnabled: false,
    isTestMode: false,
    name: '',
    expiresAt: 0,
    runsOncePerPersonIdentifier: false,
  });

  const [selectedDate, setSelectedDate] = useState<string>(
    moment().format('MMMM D, YYYY')
  );
  const [expireChecked, setExpireChecked] = useState<boolean>(false);
  const [resetExecution, setResetExecution] = useState<boolean>(false);
  const workflowCtx = useContext(WorkflowContext);
  const modalRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useOutsideClick(modalRef, onClose);

  const formik: FormikValues = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Required'),
    }),
    onSubmit: async (values) => {
      const data = {
        ...(savedData.expiresAt && savedData.expiresAt > 0
          ? { expiresAt: savedData.expiresAt }
          : {}),
        ...(savedData.defaultConversionReportingEvent !== 'Select an item'
          ? {
              defaultConversionReportingEvent:
                savedData.defaultConversionReportingEvent,
            }
          : {}),
        ...(savedData.isTestMode ? { isTestMode: savedData.isTestMode } : {}),
        isEnabled: savedData.isEnabled || false,
        name: values.name,
        workflow: workflowCtx?.workflow?.workflow,
        runsOncePerPersonIdentifier:
          savedData.runsOncePerPersonIdentifier || false,
        initialConditionGroup: workflowCtx?.workflow?.initialConditionGroup,
      };
      try {
        if (pathname.includes('create') || workflowCtx?.copyWorkflow) {
          const createWorkflowResponse = (await createWorkflow(
            data
          )) as CreateWorkflowResponse;
          if (createWorkflowResponse.status === 201) {
            Toastify('Workflow Created Successfully', 'success', 'modal2');
            if (resetExecution) {
              await resetExecutions(createWorkflowResponse.data.id);
            }
            workflowCtx?.onChangeWorkflowId(
              createWorkflowResponse.data.id || ''
            );
            navigate(`${workflow}/${createWorkflowResponse.data.id}`);
          }
        } else {
          const response = (await updateWorkflow(
            data,
            workflowCtx?.workflow?.id || ''
          )) as CreateWorkflowResponse;
          if (response.status === 200) {
            onSave();
            Toastify('Workflow Updated Successfully', 'success', 'header1');
          }
        }
      } catch (error) {
        Toastify('Updating Workflow Failed', 'error', 'header2');
      }
      onClose();
    },
  });

  const getData = async () => {
    try {
      const response = (await getEventNames()) as Response;
      if (response.status === 200) {
        setOptions([...response.data, ...eventExtraOptions]);
      }
    } catch (error) {
      console.log('error is : ', error);
    }
  };

  const inputChangeHandler = (
    name: string,
    value: string | boolean | number
  ) => {
    setSavedData((prev) => {
      return { ...prev, [name]: value };
    });
  };

  const checkStatus = (
    enabled: boolean | undefined,
    testMode: boolean | undefined
  ) => {
    if (enabled && testMode) {
      return "Test Mode (doesn't execute actions)";
    } else if (enabled) {
      return 'Live';
    } else {
      return 'Disabled';
    }
  };

  const statusChangeHandler = (value: string) => {
    let enabled = false,
      testMode = false;
    if (value === 'Disabled') {
      enabled = false;
    } else if (value === 'Live') {
      enabled = true;
    } else {
      enabled = true;
      testMode = true;
    }
    setSavedData({ ...savedData, isEnabled: enabled, isTestMode: testMode });
  };

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    const tempWorkflow: SaveWorkflowType = { ...workflowCtx?.workflow };
    if (tempWorkflow) {
      for (const prop of propertiesToDelete) {
        delete tempWorkflow[prop as keyof SaveWorkflowType];
      }

      formik.setFieldValue('name', tempWorkflow.name);
      if (tempWorkflow.expiresAt && tempWorkflow?.expiresAt > 0) {
        setExpireChecked(true);
        setSelectedDate(moment(tempWorkflow.expiresAt).format('MMMM D, YYYY'));
      }
      setSavedData(tempWorkflow);
    }
  }, [workflowCtx?.workflow]);

  useEffect(() => {
    inputChangeHandler('expiresAt', moment(selectedDate, 'MMMM DD, YYYY').valueOf());
  }, [selectedDate]);

  return (
    <form
      onSubmit={formik.handleSubmit}
      className="w-full max-w-[600px] m-auto p-5 fixed top-[14%] left-0 right-0 z-50 sm:h-[90vh] h-[70vh] no-scrollbar overflow-auto "
    >
      <div
        ref={modalRef}
        className="bg-white py-5 rounded-lg dark:bg-[#41464E]"
      >
        <div className="bg-white px-4 dark:bg-[#41464E]">
          <h2 className="sm:text-xl text-base sm:leading-5 leading-3 text-black-700 font-medium mb-5 dark:text-white">
            Save Workflow
          </h2>
          <div className=" my-auto overflow-y-auto no-scrollbar pr-3">
            <div className='mb-4'>
              <label className="text-sm font-medium leading-3 tracking-wide text-[#212529] dark:text-white ">
                Name
              </label>

              <div className="relative items-center ">
                <input
                  className="w-full px-4 py-2 text-13 leading-3  dark:bg-[#41464E]  border border-[#ced4da] dark:border-[#fff] rounded mt-2 text-[#212529] dark:text-white sm:h-[38px]"
                  type="text"
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>

              {formik.touched.name && Object.keys(formik.errors).length > 0 && (
                <div className="px-6 py-3 bg-[#f7e8e7] rounded mt-4">
                  <p className="text-sm font-normal leading-4 text-[#CB5A5A]">
                    Name Cannot be Empty
                  </p>
                </div>
              )}
            </div>
            <div>
              <label className="text-sm font-medium leading-3 tracking-wide text-[#212529] dark:text-white ">
                Default conversion reporting event
              </label>
              <div className="z-10 ">
                <SearchDropdown
                  options={options}
                  value={
                    savedData.defaultConversionReportingEvent ||
                    'Select an item'
                  }
                  onSelect={(value) =>
                    inputChangeHandler('defaultConversionReportingEvent', value)
                  }
                />
              </div>
            </div>
            <div className='mt-4'>
              <label className="text-sm font-medium leading-3 tracking-wide text-[#212529] dark:text-white ">
                Status
              </label>
              <div className="z-10 w-full relative sm:[38px] h-9 w-40">
                <Dropdown
                  options={statusOptions}
                  value={checkStatus(savedData.isEnabled, savedData.isTestMode)}
                  onSelect={(value) => statusChangeHandler(value)}
                />
              </div>
            </div>
            <div className="flex items-center mt-6">
              <input
                className="w-[14px] h-[14px] rounded-sm accent-blue-600 mr-2"
                type="checkbox"
                id="subscriber"
                name="subscriber"
                checked={savedData.runsOncePerPersonIdentifier}
                onChange={(e) =>
                  inputChangeHandler(
                    'runsOncePerPersonIdentifier',
                    e.target.checked
                  )
                }
              />
              <label
                htmlFor="subscriber"
                className="text-sm font-normal leading-4 text-[#212529] dark:text-white"
              >
                Run once per subscriber
              </label>
            </div>
            <div className="flex items-center mt-6">
              <input
                className="w-[14px] h-[14px] rounded-sm accent-blue-600 mr-2"
                type="checkbox"
                id="expire"
                name="expire"
                checked={expireChecked}
                onChange={(e) => {
                  setExpireChecked(e.target.checked);
                }}
              />
              <label
                htmlFor="expire"
                className="text-sm font-normal leading-4 text-[#212529] dark:text-white"
              >
                Expires
              </label>
            </div>
            {expireChecked && (
              <div className="relative w-2/4 flex items-center mt-4">
                <DatePicker
                  date={selectedDate}
                  onDateChange={(date) => {
                    date === ''
                      ? setSelectedDate(date)
                      : setSelectedDate(moment(date).format('MMMM D, YYYY'));
                  }}
                />
              </div>
            )}
            <div className="flex items-center mt-4">
              <input
                className="w-[14px] h-[14px] rounded-sm accent-blue-600 mr-2"
                type="checkbox"
                id="reset"
                name="reset"
                checked={resetExecution}
                onChange={(e) => setResetExecution(e.target.checked)}
              />
              <label
                htmlFor="reset"
                className="text-sm font-normal leading-4 text-[#212529] dark:text-white"
              >
                Reset workflow history (0 executions)
              </label>
            </div>
            <p className="mt-4 text-[#878A99] text-sm dark:text-white">
              Upon Save, delayed subscribers are removed and execution history
              is reset.
            </p>
          </div>
        </div>
        <div className="border-t my-5 border-[#e9ebec] dark:border-white"></div>
        <div className="px-5">
          <div className="flex justify-end items-center">
            <button
              type="submit"
              className="flex items-center justify-center text-13 font-medium leading-5 text-white rounded bg-primary px-4 py-2.5 min-w-[80px] mr-4"
              disabled={Object.keys(formik.errors).length > 0}
            >
              Save
            </button>
            <button
              type="button"
              className="text-center sm:text-13 text-xs py-2.5 px-4 h-11 flex justify-center items-center w-28 font-medium leading-5 rounded-md bg-[#f3f6f9] hover:bg-[#cfd1d4] transition-bg text-[#212529]"
              onClick={() => onClose()}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};
export default SaveModal;
